import Phaser from "phaser";
import { audioManager } from "../audio/AudioManager";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { preloadBorgSprites } from "./game/BorgCube";
import { preloadPlayerSprites } from "./game/Player";
import { Starfield } from "./game/Starfield";

// Flyby configurations
const FLYBY_SPRITES = [
  { key: "voyager-pristine", scale: 0.15 },
  { key: "voyager-pristine", scale: 0.25 },
  { key: "defiant-pristine", scale: 0.15 },
  { key: "defiant-pristine", scale: 0.3 },
  { key: "borg-active", scale: 0.12 },
  { key: "borg-active", scale: 0.2 },
  { key: "borg-active", scale: 0.35 },
];

export class SplashScene extends Phaser.Scene {
  private starfield!: Starfield;
  private titleText!: Phaser.GameObjects.Text;
  private promptText!: Phaser.GameObjects.Text;
  private canStart = false;
  private currentFlyby: Phaser.GameObjects.Sprite | null = null;
  private flybyTimer: Phaser.Time.TimerEvent | null = null;

  constructor() {
    super({ key: "SplashScene" });
  }

  preload(): void {
    // Load ship and borg sprites for flyby
    preloadPlayerSprites(this.load);
    preloadBorgSprites(this.load);
  }

  create(): void {
    // Create starfield background
    this.starfield = new Starfield(this);
    this.starfield.create(150);

    // Draw Starfleet logo (static, no rotation)
    this.createStarfleetLogo();

    // Title
    this.titleText = this.add
      .text(GAME_WIDTH / 2, 180, "VOYAGER", {
        fontSize: "64px",
        color: "#ff9900",
        fontFamily: "Courier New",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 240, "VS. THE BORG", {
        fontSize: "48px",
        color: "#00ff00",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    // Subtitle
    this.add
      .text(GAME_WIDTH / 2, 310, '"There\'s coffee in that nebula!"', {
        fontSize: "18px",
        color: "#aaaaaa",
        fontFamily: "Courier New",
        fontStyle: "italic",
      })
      .setOrigin(0.5);

    // Instructions
    this.add
      .text(GAME_WIDTH / 2, 420, "CONTROLS", {
        fontSize: "20px",
        color: "#ffcc00",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    this.add
      .text(
        GAME_WIDTH / 2,
        460,
        "ARROWS / WASD - Rotate & Thrust\nSPACE - Phasers  |  E - Torpedoes",
        {
          fontSize: "16px",
          color: "#99ccff",
          fontFamily: "Courier New",
          align: "center",
        },
      )
      .setOrigin(0.5);

    // Start prompt - appears after delay
    this.promptText = this.add
      .text(GAME_WIDTH / 2, 540, ">>> PRESS ANY KEY TO ENGAGE <<<", {
        fontSize: "24px",
        color: "#ff9900",
        fontFamily: "Courier New",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setAlpha(0);

    // Animate title in
    this.tweens.add({
      targets: this.titleText,
      y: 180,
      alpha: { from: 0, to: 1 },
      duration: 1000,
      ease: "Power2",
    });

    // Show start prompt after delay
    this.time.delayedCall(1500, () => {
      this.canStart = true;
      this.startPromptFlash();
    });

    // Start flyby spawner
    this.time.delayedCall(2000, () => this.scheduleFlyby());

    // Input handlers
    this.input.keyboard?.on("keydown", () => this.startGame());
    this.input.on("pointerdown", () => this.startGame());

    // Play intro jingle
    this.time.delayedCall(500, () => {
      audioManager.init();
      this.playIntroJingle();
    });
  }

  private startPromptFlash(): void {
    // Fade in
    this.tweens.add({
      targets: this.promptText,
      alpha: 1,
      duration: 300,
    });

    // Flash animation - alternates between bright and dim with color shift
    this.tweens.add({
      targets: this.promptText,
      alpha: 0.3,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
      onYoyo: () => {
        // Cycle colors: orange -> yellow -> white -> orange
        const colors = ["#ff9900", "#ffcc00", "#ffffff"];
        const currentColor = this.promptText.style.color;
        const idx = colors.indexOf(currentColor as string);
        const nextIdx = (idx + 1) % colors.length;
        this.promptText.setColor(colors[nextIdx]!);
      },
    });

    // Scale pulse
    this.tweens.add({
      targets: this.promptText,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  private scheduleFlyby(): void {
    // Random delay between flybys (3-6 seconds)
    const delay = 3000 + Math.random() * 3000;

    this.flybyTimer = this.time.delayedCall(delay, () => {
      this.spawnFlyby();
      this.scheduleFlyby();
    });
  }

  private spawnFlyby(): void {
    // Don't spawn if one is already active
    if (this.currentFlyby) return;

    // Pick random sprite config
    const config = FLYBY_SPRITES[Math.floor(Math.random() * FLYBY_SPRITES.length)]!;

    // Determine path: either horizontal or diagonal
    const isHorizontal = Math.random() > 0.3;
    const fromLeft = Math.random() > 0.5;

    let startX: number, startY: number, endX: number, endY: number;

    if (isHorizontal) {
      // Horizontal path at random Y
      startY = 100 + Math.random() * 400;
      endY = startY + (Math.random() - 0.5) * 100;
      startX = fromLeft ? -100 : GAME_WIDTH + 100;
      endX = fromLeft ? GAME_WIDTH + 100 : -100;
    } else {
      // Diagonal path
      const fromTop = Math.random() > 0.5;
      startX = fromLeft ? -100 : GAME_WIDTH + 100;
      startY = fromTop ? -100 : GAME_HEIGHT + 100;
      endX = fromLeft ? GAME_WIDTH + 100 : -100;
      endY = fromTop ? GAME_HEIGHT + 100 : -100;
    }

    // Calculate rotation based on travel direction
    const travelAngle = Math.atan2(endY - startY, endX - startX);
    // Ships point DOWN at rotation=0, so subtract PI/2 to face travel direction
    // Borg cubes are symmetric so rotation doesn't matter much
    const isShip = config.key.includes("voyager") || config.key.includes("defiant");
    const rotation = isShip ? travelAngle - Math.PI / 2 : travelAngle;

    // Create the sprite
    this.currentFlyby = this.add.sprite(startX, startY, config.key);
    this.currentFlyby.setScale(config.scale);
    this.currentFlyby.setRotation(rotation);
    this.currentFlyby.setDepth(-1); // Behind UI

    // Animate across screen
    const duration = 4000 + Math.random() * 2000;
    this.tweens.add({
      targets: this.currentFlyby,
      x: endX,
      y: endY,
      duration,
      ease: "Linear",
      onComplete: () => {
        if (this.currentFlyby) {
          this.currentFlyby.destroy();
          this.currentFlyby = null;
        }
      },
    });
  }

  update(): void {
    // Slowly drift the starfield
    this.starfield.update(new Phaser.Math.Vector2(20, 10));
  }

  private startGame(): void {
    if (!this.canStart) return;
    this.canStart = false;

    // Stop flyby timer
    if (this.flybyTimer) {
      this.flybyTimer.destroy();
    }

    // Flash effect
    this.cameras.main.flash(300, 255, 153, 0);

    // Transition to ship selection
    this.time.delayedCall(300, () => {
      this.scene.start("ShipSelectScene");
    });
  }

  private createStarfleetLogo(): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = 100;

    // Outer ring (static)
    const outerRing = this.add.graphics();
    outerRing.lineStyle(3, 0xff9900);
    outerRing.strokeCircle(centerX, centerY, 50);

    // Inner arrowhead shape (simplified Starfleet delta)
    const delta = this.add.graphics();
    delta.fillStyle(0xff9900);
    delta.beginPath();
    delta.moveTo(centerX, centerY - 35);
    delta.lineTo(centerX - 25, centerY + 25);
    delta.lineTo(centerX, centerY + 10);
    delta.lineTo(centerX + 25, centerY + 25);
    delta.closePath();
    delta.fillPath();

    // Star in the center
    delta.fillStyle(0xffffff);
    delta.fillCircle(centerX, centerY - 5, 5);

    // Glow effect
    const glow = this.add.graphics();
    glow.fillStyle(0xff9900, 0.1);
    glow.fillCircle(centerX, centerY, 60);
  }

  private playIntroJingle(): void {
    // Use Web Audio API directly for a simple fanfare
    const ctx = new AudioContext();

    const notes = [
      { freq: 523.25, start: 0, duration: 0.2 }, // C5
      { freq: 659.25, start: 0.2, duration: 0.2 }, // E5
      { freq: 783.99, start: 0.4, duration: 0.4 }, // G5
      { freq: 1046.5, start: 0.8, duration: 0.6 }, // C6
    ];

    for (const note of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = "triangle";
      osc.frequency.value = note.freq;

      const startTime = ctx.currentTime + note.start;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(
        0.01,
        startTime + note.duration - 0.05,
      );

      osc.start(startTime);
      osc.stop(startTime + note.duration);
    }
  }
}
